// src/features/affiliate/CampaignDetailRail.tsx
// A partner's campaign: tracking code/url, referral links, click stats, and
// a "test my link" action that resolves the tracking code without leaving
// the page.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useAffiliateCampaign, useAffiliateReferralLink, useAffiliateTracking, useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { FormRow } from '@/components/form';
import { useScope } from '@/hooks/useScope';
import type { Campaign, Click } from '@core/types/affiliate';
import { CampaignStatusBadge } from './badges';
import { errMessage, formatMoney, listParams, makeEntity } from './format';

interface CampaignDetailRailProps {
  campaign: Campaign;
  onClose: () => void;
  onEdit: () => void;
  onChanged: () => void;
}

export const CampaignDetailRail = observer(({ campaign, onClose, onEdit, onChanged }: CampaignDetailRailProps) => {
  const { t, locale } = useI18n();
  const scope = useScope();
  const campaignStore = useAffiliateCampaign();
  const linkStore = useAffiliateReferralLink();
  const trackingStore = useAffiliateTracking();

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newLinkOpen, setNewLinkOpen] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkDest, setLinkDest] = useState('');
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recentClicks, setRecentClicks] = useState<Click[]>([]);

  const campaignId = campaign.entity?.id ?? '';

  const loadLinks = () => {
    linkStore.ListReferralLinks(listParams({ limit: 50 }, { campaign_id: campaignId })).catch(() => {});
  };

  useEffect(() => {
    loadLinks();
    trackingStore
      .ListClicks(listParams({ limit: 10 }, { campaign_id: campaignId }))
      .then(() => setRecentClicks(trackingStore.items))
      .catch(() => setRecentClicks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const toggleStatus = async () => {
    if (!campaign.entity) return;
    setBusy(true);
    setError(null);
    try {
      if (campaign.status === 'CAMPAIGN_STATUS_ACTIVE') {
        await campaignStore.PauseCampaign(campaignId, { entity: campaign.entity });
      } else {
        await campaignStore.ActivateCampaign(campaignId, { entity: campaign.entity });
      }
      onChanged();
    } catch (e) {
      setError(errMessage(e, t('affiliate.campaigns.saveError')));
    } finally {
      setBusy(false);
    }
  };

  const createLink = async () => {
    if (!linkName.trim() || !linkDest.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await linkStore.CreateReferralLink({
        entity: makeEntity('', 'referral_link', scope),
        campaign_id: campaignId,
        name: linkName.trim(),
        destination_url: linkDest.trim(),
        metadata: {},
      });
      setLinkName('');
      setLinkDest('');
      setNewLinkOpen(false);
      loadLinks();
    } catch (e) {
      setError(errMessage(e, t('affiliate.campaigns.linkSaveError')));
    } finally {
      setBusy(false);
    }
  };

  const testLink = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await trackingStore.TrackClick(campaign.tracking_code);
      setTestResult(res.redirect_url || t('affiliate.campaigns.testLinkOk'));
    } catch (e) {
      setTestResult(errMessage(e, t('affiliate.campaigns.testLinkError')));
    } finally {
      setTesting(false);
    }
  };

  const copyTrackingUrl = async () => {
    try {
      await navigator.clipboard.writeText(campaign.tracking_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — URL is still visible
    }
  };

  return (
    <aside className="w-full shrink-0 space-y-4 overflow-y-auto border-l border-border p-4 lg:w-96">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">{t('affiliate.campaigns.detailTitle')}</h2>
        <button onClick={onClose} aria-label={t('common.close')} className="rounded-md p-1 text-muted-foreground hover:bg-muted">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">{campaign.name}</h3>
          <CampaignStatusBadge status={campaign.status} />
        </div>
        <p className="mt-1 truncate font-mono text-xs text-muted-foreground">{campaign.tracking_url}</p>
      </div>

      <div className="flex gap-1.5">
        <Button size="sm" variant="secondary" onClick={copyTrackingUrl}>
          {copied ? t('affiliate.campaigns.copied') : t('affiliate.campaigns.copyUrl')}
        </Button>
        <Button size="sm" variant="secondary" onClick={testLink} isLoading={testing}>
          {t('affiliate.campaigns.testLink')}
        </Button>
        <Button size="sm" variant="secondary" onClick={onEdit}>
          {t('common.edit')}
        </Button>
        <Button size="sm" variant={campaign.status === 'CAMPAIGN_STATUS_ACTIVE' ? 'danger' : 'primary'} onClick={toggleStatus} isLoading={busy}>
          {campaign.status === 'CAMPAIGN_STATUS_ACTIVE' ? t('affiliate.campaigns.pause') : t('affiliate.campaigns.activate')}
        </Button>
      </div>

      {testResult && (
        <Alert variant="info" dismissible onDismiss={() => setTestResult(null)}>
          <span className="break-all">{testResult}</span>
        </Alert>
      )}

      <div className="grid grid-cols-3 gap-2 text-sm">
        <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.clicks')}</p><p className="text-foreground">{campaign.total_clicks}</p></div>
        <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.conversions')}</p><p className="text-foreground">{campaign.total_conversions}</p></div>
        <div><p className="text-xs text-muted-foreground">{t('affiliate.dashboard.earned')}</p><p className="text-foreground">{formatMoney(campaign.total_earned, locale)}</p></div>
      </div>

      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-foreground">{t('affiliate.campaigns.referralLinks')}</h4>
          <Button size="sm" variant="secondary" onClick={() => setNewLinkOpen((v) => !v)}>
            {t('affiliate.campaigns.newLink')}
          </Button>
        </div>

        {newLinkOpen && (
          <div className="mt-2 space-y-1 rounded-md border border-border p-2">
            <FormRow label={t('common.name')} labelWidth="sm">
              <input
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <FormRow label={t('affiliate.campaigns.destinationUrl')} labelWidth="sm">
              <input
                value={linkDest}
                onChange={(e) => setLinkDest(e.target.value)}
                placeholder="https://…"
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              />
            </FormRow>
            <div className="pt-1">
              <Button size="sm" onClick={createLink} isLoading={busy} disabled={!linkName.trim() || !linkDest.trim()}>
                {t('common.create')}
              </Button>
            </div>
          </div>
        )}

        <ul className="mt-2 space-y-1.5">
          {linkStore.items.map((link) => (
            <li key={link.entity?.id} className="rounded-md border border-border p-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">{link.name}</span>
                <Badge variant="default">{link.total_clicks} {t('affiliate.dashboard.clicks').toLowerCase()}</Badge>
              </div>
              <p className="mt-0.5 truncate font-mono text-muted-foreground">{link.short_url || link.destination_url}</p>
            </li>
          ))}
          {linkStore.items.length === 0 && <p className="text-xs text-muted-foreground">{t('affiliate.campaigns.noLinks')}</p>}
        </ul>
      </div>

      <div className="border-t border-border pt-3">
        <h4 className="text-sm font-medium text-foreground">{t('affiliate.campaigns.recentClicks')}</h4>
        <ul className="mt-2 space-y-1 text-xs">
          {recentClicks.map((c) => (
            <li key={c.entity?.id} className="flex items-center justify-between text-muted-foreground">
              <span>{c.country || '—'} · {c.device_type || '—'}</span>
              <span>{c.is_unique ? t('affiliate.campaigns.uniqueClick') : t('affiliate.campaigns.repeatClick')}</span>
            </li>
          ))}
          {recentClicks.length === 0 && <p className="text-xs text-muted-foreground">{t('affiliate.campaigns.noClicks')}</p>}
        </ul>
      </div>
    </aside>
  );
});
